import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// Constants
const CHARACTER_SIZE = 60;
const GEM_SIZE = 30;
const ENEMY_SIZE = 40;

// Static images
const DEFAULT_AVATAR = new Image();
DEFAULT_AVATAR.src = '/tybee-circle.png';

const GEM_IMAGE = new Image();
GEM_IMAGE.src = '/gem.png';

const GameCanvas = ({
  canvasSize,
  position,
  isMoving,
  wobbleOffset,
  avatarImage,
  modules,
  activeModule,
  getModulePositions,
  gems,
  collectedGems,
  enemies,
  enemySpeedMultiplier,
  isInvulnerable
}) => {
  const canvasRef = useRef(null);

  // Ensure consistent canvas dimensions
  const canvasWidth = 1200;
  const canvasHeight = 700;

  // Calculate current multiplier based on collected gems
  const calculateMultiplier = () => {
    if (collectedGems < 10) return 1;
    return 1 + Math.floor(collectedGems / 10);
  };

  // Draw the game world
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions explicitly
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Clear the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background elements (optional)
    // ...

    // Draw modules
    const modulePositions = getModulePositions();
    modulePositions.forEach((modulePos, index) => {
      const isRewardsModule = modulePos.isRewards;

      // Skip regular modules if we're past the available modules
      if (!isRewardsModule && index >= modules.length) return;

      // Skip rewards module for non-authenticated users
      if (isRewardsModule && !avatarImage) return;

      // Draw module platform
      ctx.beginPath();
      ctx.arc(modulePos.x, modulePos.y, isRewardsModule ? 40 : 50, 0, Math.PI * 2);

      // Different colors for different modules with gradient
      const colors = [
        ['#4299e1', '#3182ce'], // blue
        ['#48bb78', '#38a169'], // green
        ['#ed8936', '#dd6b20'], // orange
        ['#ed64a6', '#d53f8c'], // pink
        ['#667eea', '#5a67d8'], // indigo
        ['#38b2ac', '#319795'],  // teal
        ['#f6e05e', '#d69e2e']   // yellow/gold for rewards
      ];

      const colorIndex = isRewardsModule ? 6 : (index % 6);

      const gradient = ctx.createRadialGradient(
        modulePos.x, modulePos.y, 10,
        modulePos.x, modulePos.y, isRewardsModule ? 40 : 50
      );
      gradient.addColorStop(0, colors[colorIndex][0]);
      gradient.addColorStop(1, colors[colorIndex][1]);

      ctx.fillStyle = gradient;
      ctx.fill();

      // Glow effect for active module
      if (activeModule === index) {
        ctx.shadowBlur = 15;
        ctx.shadowColor = colors[colorIndex][0];
      }

      ctx.lineWidth = activeModule === index ? 4 : 2;
      ctx.strokeStyle = activeModule === index ? '#ffffff' : 'rgba(255, 255, 255, 0.7)';
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Draw module text - simplified, cleaner approach
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      if (isRewardsModule) {
        // Draw rewards module label
        ctx.font = 'bold 14px Arial';
        ctx.fillText("Rewards", modulePos.x, modulePos.y);

        // Draw a small coin icon
        ctx.font = '16px Arial';
        ctx.fillText("🪙", modulePos.x, modulePos.y - 20);
      } else {
        // Get module title from the modules data
        const moduleTitle = modules[index].title;

        // Process the title to fit in the circle
        let displayTitle = moduleTitle;

        // If title has multiple words, try to use just the first word if it's too long
        if (moduleTitle.length > 10 && moduleTitle.includes(' ')) {
          const firstWord = moduleTitle.split(' ')[0];
          displayTitle = firstWord.length > 3 ? firstWord : moduleTitle.substring(0, 10);
        }
        // If still too long, truncate
        else if (moduleTitle.length > 10) {
          displayTitle = moduleTitle.substring(0, 8) + '..';
        }

        // Draw the module label
        ctx.font = 'bold 16px Arial';
        ctx.fillText(displayTitle, modulePos.x, modulePos.y);

        // Add a small icon or indicator for premium modules
        if (modules[index].premium) {
          ctx.font = '12px Arial';
          ctx.fillText('✦', modulePos.x, modulePos.y + 20);
        }
      }
    });

    // Draw gems
    gems.forEach(gem => {
      ctx.save();
      ctx.translate(gem.x, gem.y);
      ctx.rotate(gem.rotation);

      // Try to draw the jam image
      if (GEM_IMAGE.complete && GEM_IMAGE.naturalHeight !== 0) {
        ctx.drawImage(
          GEM_IMAGE,
          -GEM_SIZE/2,
          -GEM_SIZE/2,
          GEM_SIZE,
          GEM_SIZE
        );
      }
      // Fallback to the original diamond shape if both images fail
      else {
        // Create a diamond/gem shape
        const gemSize = GEM_SIZE / 2;
        ctx.beginPath();
        ctx.moveTo(0, -gemSize);
        ctx.lineTo(gemSize, 0);
        ctx.lineTo(0, gemSize);
        ctx.lineTo(-gemSize, 0);
        ctx.closePath();

        // Create a gradient for the gem
        const gemGradient = ctx.createLinearGradient(-gemSize, -gemSize, gemSize, gemSize);
        gemGradient.addColorStop(0, '#9f7aea');    // Purple
        gemGradient.addColorStop(0.5, '#667eea');  // Indigo
        gemGradient.addColorStop(1, '#4299e1');    // Blue

        ctx.fillStyle = gemGradient;
        ctx.fill();

        // Add a stroke
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
        ctx.stroke();
      }

      // Add a sparkle effect regardless of which rendering method was used
      const sparkleTime = Date.now() / 200;
      if (Math.sin(sparkleTime + gem.id) > 0.7) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.beginPath();
        ctx.arc(0, 0, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    });

    // Draw enemies
    enemies.forEach(enemy => {
      if (!enemy.active) return;

      ctx.save();

      // Draw enemy body
      ctx.beginPath();
      ctx.arc(enemy.x, enemy.y, ENEMY_SIZE/2, 0, Math.PI * 2);

      // Create gradient for enemy - color intensity based on speed
      const gradient = ctx.createRadialGradient(
        enemy.x, enemy.y, ENEMY_SIZE/4,
        enemy.x, enemy.y, ENEMY_SIZE/2
      );

      // Make enemies glow more intensely as they get faster
      const baseColor = enemy.color;
      const glowIntensity = Math.min(1, (enemySpeedMultiplier - 1) * 2); // 0 to 1 based on speed

      // Mix the base color with a brighter color based on speed
      gradient.addColorStop(0, baseColor);
      gradient.addColorStop(1, glowIntensity > 0.3 ? '#ff0000' : '#000');

      ctx.fillStyle = gradient;
      ctx.fill();

      // Add a glow effect for faster enemies
      if (enemySpeedMultiplier > 1.5) {
        ctx.shadowBlur = 10 * (enemySpeedMultiplier - 1);
        ctx.shadowColor = 'rgba(255, 0, 0, 0.7)';
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // Draw enemy eyes
      ctx.fillStyle = '#fff';

      // Calculate eye positions based on direction to player
      const dx = position.x - enemy.x;
      const dy = position.y - enemy.y;
      const angle = Math.atan2(dy, dx);

      const eyeOffsetX = Math.cos(angle) * ENEMY_SIZE/4;
      const eyeOffsetY = Math.sin(angle) * ENEMY_SIZE/4;

      // Left eye
      ctx.beginPath();
      ctx.arc(
        enemy.x + eyeOffsetX - Math.sin(angle) * ENEMY_SIZE/5,
        enemy.y + eyeOffsetY + Math.cos(angle) * ENEMY_SIZE/5,
        ENEMY_SIZE/8, 0, Math.PI * 2
      );
      ctx.fill();

      // Right eye
      ctx.beginPath();
      ctx.arc(
        enemy.x + eyeOffsetX + Math.sin(angle) * ENEMY_SIZE/5,
        enemy.y + eyeOffsetY - Math.cos(angle) * ENEMY_SIZE/5,
        ENEMY_SIZE/8, 0, Math.PI * 2
      );
      ctx.fill();

      ctx.restore();
    });

    // Draw character shadow
    const drawX = position.x;
    const drawY = position.y + wobbleOffset;

    // Draw oval shadow under character
    ctx.beginPath();
    const shadowWidth = CHARACTER_SIZE/2 * (1 + Math.abs(wobbleOffset/20));
    const shadowHeight = CHARACTER_SIZE/6 * (1 - Math.abs(wobbleOffset/20));
    ctx.ellipse(position.x, position.y + CHARACTER_SIZE/2 - 5, shadowWidth, shadowHeight, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)';
    ctx.fill();

    // Draw character with invulnerability effect if needed
    if (isInvulnerable) {
      // Flashing shield effect
      const shieldOpacity = 0.3 + Math.sin(Date.now() / 100) * 0.2;
      ctx.beginPath();
      ctx.arc(position.x, position.y, CHARACTER_SIZE/2 + 10, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(100, 149, 237, ${shieldOpacity})`;
      ctx.fill();
    }

    // Draw character
    if (avatarImage) {
      // Draw avatar image with circular crop and wobble
      ctx.save();
      ctx.beginPath();
      ctx.arc(drawX, drawY, CHARACTER_SIZE/2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      ctx.drawImage(
        avatarImage,
        drawX - CHARACTER_SIZE/2,
        drawY - CHARACTER_SIZE/2,
        CHARACTER_SIZE,
        CHARACTER_SIZE
      );

      ctx.restore();

      // Draw border around avatar
      ctx.beginPath();
      ctx.arc(drawX, drawY, CHARACTER_SIZE/2, 0, Math.PI * 2);
      ctx.lineWidth = 3;
      ctx.strokeStyle = isInvulnerable ? '#4299e1' : '#3867d6';
      ctx.stroke();
    } else {
      // Use the static default avatar if available
      if (DEFAULT_AVATAR.complete && DEFAULT_AVATAR.naturalHeight !== 0) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(drawX, drawY, CHARACTER_SIZE/2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(
          DEFAULT_AVATAR,
          drawX - CHARACTER_SIZE/2,
          drawY - CHARACTER_SIZE/2,
          CHARACTER_SIZE,
          CHARACTER_SIZE
        );

        ctx.restore();

        // Draw border around avatar
        ctx.beginPath();
        ctx.arc(drawX, drawY, CHARACTER_SIZE/2, 0, Math.PI * 2);
        ctx.lineWidth = 3;
        ctx.strokeStyle = isInvulnerable ? '#4299e1' : '#3867d6';
        ctx.stroke();
      } else {
        // Fallback to circle if image not loaded
        ctx.beginPath();
        ctx.arc(drawX, drawY, CHARACTER_SIZE/2, 0, Math.PI * 2);
        ctx.fillStyle = isInvulnerable ? '#4299e1' : '#4b7bec';
        ctx.fill();
        ctx.lineWidth = 3;
        ctx.strokeStyle = isInvulnerable ? '#4299e1' : '#3867d6';
        ctx.stroke();

        // Draw character face
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(drawX - 10, drawY - 5, 6, 0, Math.PI * 2); // Left eye
        ctx.arc(drawX + 10, drawY - 5, 6, 0, Math.PI * 2); // Right eye
        ctx.fill();

        ctx.beginPath();
        ctx.arc(drawX, drawY + 10, 10, 0, Math.PI); // Smile
        ctx.stroke();
      }
    }

    // Draw gem counter and multiplier
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(10, 10, 120, 40);

    // Draw gem count
    ctx.fillStyle = '#9f7aea'; // Purple color for gems
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`Gems: ${collectedGems}`, 20, 30);

    // Draw high score in the middle top
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(canvas.width/2 - 150, 10, 300, 40);

    // Get the high score from localStorage or use current score if higher
    const storedHighScore = parseInt(localStorage.getItem('tyb_high_score') || '0');
    const currentHighScore = Math.max(storedHighScore, collectedGems);

    // Update localStorage if we have a new high score
    if (collectedGems > storedHighScore) {
      localStorage.setItem('tyb_high_score', collectedGems.toString());
    }

    // Draw trophy icon
    ctx.fillStyle = '#f6e05e'; // Gold color for trophy
    ctx.beginPath();
    const trophyX = canvas.width/2 - 70;
    const trophyY = 30;
    // Trophy cup
    ctx.moveTo(trophyX - 8, trophyY - 8);
    ctx.lineTo(trophyX + 8, trophyY - 8);
    ctx.lineTo(trophyX + 6, trophyY);
    ctx.lineTo(trophyX - 6, trophyY);
    ctx.closePath();
    ctx.fill();
    // Trophy base
    ctx.fillRect(trophyX - 3, trophyY, 6, 8);
    ctx.fillRect(trophyX - 6, trophyY + 8, 12, 2);

    // Draw high score text
    ctx.fillStyle = '#f6e05e'; // Gold color for high score
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`High Score: ${currentHighScore}`, canvas.width/2 + 30, 30);

    // Draw leaderboard button at bottom left
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
    ctx.fillRect(20, canvas.height - 60, 180, 40);

    // Draw trophy icon for leaderboard
    ctx.fillStyle = '#f6e05e'; // Gold color for trophy
    ctx.beginPath();
    const leaderboardTrophyX = 40;
    const leaderboardTrophyY = canvas.height - 40;
    // Trophy cup
    ctx.moveTo(leaderboardTrophyX - 8, leaderboardTrophyY - 8);
    ctx.lineTo(leaderboardTrophyX + 8, leaderboardTrophyY - 8);
    ctx.lineTo(leaderboardTrophyX + 6, leaderboardTrophyY);
    ctx.lineTo(leaderboardTrophyX - 6, leaderboardTrophyY);
    ctx.closePath();
    ctx.fill();
    // Trophy base
    ctx.fillRect(leaderboardTrophyX - 3, leaderboardTrophyY, 6, 8);
    ctx.fillRect(leaderboardTrophyX - 6, leaderboardTrophyY + 8, 12, 2);

    // Draw leaderboard text
    ctx.fillStyle = '#9f7aea'; // Purple color for leaderboard
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('View Leaderboard', 60, canvas.height - 40);

    // Update click handler for leaderboard
    canvas.onclick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Check if click is within the leaderboard button area
      if (x >= 20 && x <= 200 && y >= canvas.height - 60 && y <= canvas.height - 20) {
        window.location.href = '/game_leaderboard';
      }
    };

    // Draw multiplier if applicable
    const multiplier = calculateMultiplier();
    if (multiplier > 1) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(140, 10, 140, 40);

      ctx.fillStyle = '#f6e05e'; // Gold color for multiplier
      ctx.font = 'bold 20px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${multiplier}x Coins!`, 150, 30);
    }

  }, [canvasSize, position, isMoving, wobbleOffset, avatarImage, modules, activeModule, getModulePositions, gems, collectedGems, enemies, enemySpeedMultiplier, isInvulnerable]);

  return (
    <canvas
      ref={canvasRef}
      width={canvasWidth}
      height={canvasHeight}
      className="w-full h-full"
    />
  );
};

GameCanvas.propTypes = {
  canvasSize: PropTypes.object.isRequired,
  position: PropTypes.object.isRequired,
  isMoving: PropTypes.bool.isRequired,
  wobbleOffset: PropTypes.number.isRequired,
  avatarImage: PropTypes.object,
  modules: PropTypes.array.isRequired,
  activeModule: PropTypes.number,
  getModulePositions: PropTypes.func.isRequired,
  gems: PropTypes.array.isRequired,
  collectedGems: PropTypes.number.isRequired,
  enemies: PropTypes.array,
  enemySpeedMultiplier: PropTypes.number,
  isInvulnerable: PropTypes.bool
};

GameCanvas.defaultProps = {
  enemies: [],
  enemySpeedMultiplier: 1.0,
  isInvulnerable: false
};

export default GameCanvas;